import * as React from 'react';
import { Button, ButtonTheme, VisualSize } from '@cian/components';
import * as styles from './styles.css';
import { Tooltip, TooltipPosition } from '@cian/tooltip-component';
import { TextField } from '@cian/text-field-component';
import { Checkbox } from '@cian/checkbox-component';
import { IJsonQuery } from '../../../../json_query';
import { validateEmailFormat } from '../../../../utils/format';
import { trackingSaveFilterSend, trackSubscribed } from '../../../../filters/components/save/tracking';
import { mapNotificationFrequency, TNotificationFrequency } from '../../../../filters/components/save/popup';
import { trackPreInfiniteSaveSearchShow, trackSaveSearchFromPreInfinite } from './tracking';

const FREQUENCY = 1;
const FAKE_EMAIL_REGEX = /@social\.cian\.ru$/;
const FLOCKTORY_KEY_EVENT = 'show_push_cian_site';

export interface IRequestSubscribeParams {
  email: string;
  title: string;
  subscribeNews: boolean;
  jsonQuery: IJsonQuery;
  notificationFrequency: TNotificationFrequency;
}

export interface ISaveSearchContentProps {
  userEmail: string | null;
  jsonQuery: IJsonQuery;
  isAuthenticated: boolean;
  showPushNotification: boolean | undefined;
  pageHeader: string | null;
  metaHeader?: string;

  onOffersSearch(): void;
  onRequestSubscribe(params: IRequestSubscribeParams): Promise<void>;
}

export interface ISaveSearchContentState {
  email: string;
  emailInvalid: boolean;
  emailError: string;
  isNews: boolean;
  isLoading: boolean;
  isTooltipOpen: boolean;
  isSuccess: boolean;
  isPushCheck: boolean;
  isModalVisible: boolean;
  wasSubscribed: boolean;
}

export class SaveSearchContent extends React.Component<ISaveSearchContentProps, ISaveSearchContentState> {
  private TITLE = '';

  public constructor(props: ISaveSearchContentProps) {
    super(props);

    this.TITLE = props.metaHeader || props.pageHeader || '';
    this.state = {
      email: props.userEmail && !FAKE_EMAIL_REGEX.test(props.userEmail) ? props.userEmail : '',
      emailInvalid: false,
      emailError: '',
      isNews: true,
      isLoading: false,
      isTooltipOpen: false,
      isSuccess: false,
      isModalVisible: false,
      isPushCheck: true,
      wasSubscribed: false,
    };
  }

  private handleEmailChange = (value: string) => {
    this.setState({
      email: value,
      emailInvalid: false,
      emailError: '',
    });
  };

  private getEmailError = () => {
    const { email } = this.state;

    if (!email.trim()) {
      //к сожелению не удается кастомизировать ширину тултипа за счет переданных в компонент стилей
      return '\xa0Введите email\xa0';
    } else if (!validateEmailFormat(email)) {
      return 'Неверный e-mail адрес';
    }

    return '';
  };

  private checkUserEmail() {
    const { isAuthenticated, userEmail } = this.props;
    const { email } = this.state;

    return isAuthenticated && userEmail && email.trim().toLowerCase() === userEmail.toLowerCase();
  }

  private handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { jsonQuery } = this.props;
    trackingSaveFilterSend(jsonQuery);

    const { onOffersSearch, showPushNotification } = this.props;
    const { isPushCheck } = this.state;

    this.setState({
      isLoading: true,
    });

    const emailError = this.getEmailError();

    if (emailError) {
      this.setState({
        emailInvalid: Boolean(emailError),
        emailError,
        isLoading: false,
      });

      return;
    }
    try {
      await this.saveFilter();

      trackSubscribed(FREQUENCY, Boolean(showPushNotification), isPushCheck);
      //TODO: исправить после удаления flocktory
      this.setState({ isSuccess: true });

      this.setState(
        {
          isLoading: false,
        },
        () => {
          if (this.state.isPushCheck && window.dataLayer) {
            /**
             * Пушим кастомное событие, на которое настроен flocktory триггер,
             * по этому событию показываем системный попап разрешения на пуш уведомления
             */
            window.dataLayer.push({ event: FLOCKTORY_KEY_EVENT });
          }

          if (onOffersSearch) {
            onOffersSearch();
          }

          trackSaveSearchFromPreInfinite();
          this.setState({ wasSubscribed: true });
        },
      );
    } catch (e) {
      this.setState({
        isLoading: false,
      });
    } finally {
      if (window.__reloadHeader__) {
        window.__reloadHeader__();
      }
    }
  };

  private saveFilter() {
    const { jsonQuery, onRequestSubscribe } = this.props;
    const { email, isNews } = this.state;

    return onRequestSubscribe({
      email,
      jsonQuery,
      subscribeNews: isNews,
      title: this.TITLE,
      notificationFrequency: mapNotificationFrequency(FREQUENCY),
    });
  }

  public componentDidMount() {
    trackPreInfiniteSaveSearchShow();
  }

  public render() {
    const { showPushNotification } = this.props;
    const { email, isLoading, emailInvalid, isNews, isPushCheck } = this.state;

    return (
      <>
        <h3 className={styles['banner-text-bold']}>
          Сейчас это все объявления по вашему поиску.
          <br />
          Подпишитесь на обновления, и мы сообщим вам, если что-то появится
        </h3>
        {this.state.wasSubscribed ? (
          <div className={styles['success-email']}>
            {this.checkUserEmail()
              ? 'Мы оповестим вас, когда появятся новые объявления по вашему поиску'
              : `На ${email} отправлено письмо для активации подписки`}
          </div>
        ) : (
          <form action="#" onSubmit={this.handleFormSubmit}>
            <div className={styles['banner-form-row']}>
              <Tooltip tooltipContent={this.state.emailError} position={TooltipPosition.RIGHT_CENTER}>
                <TextField
                  // ref={(emailTextfield) => this.emailTextfield = emailTextfield}
                  placeholder="Ваш email"
                  containerStyle={styles['input-container']}
                  inputStyle={styles['input']}
                  value={email}
                  disabled={isLoading}
                  invalid={emailInvalid}
                  onValueChange={this.handleEmailChange}
                />
              </Tooltip>
              <Button type="submit" theme={ButtonTheme.BLUE_SOLID} size={VisualSize.SMALL}>
                Подписаться
              </Button>
            </div>
            <Checkbox
              containerStyle={styles['checkbox']}
              label="Получать новости Cian.ru "
              value={isNews}
              onValueChange={value => this.setState({ isNews: value })}
            />
            {showPushNotification && (
              <Checkbox
                containerStyle={styles['checkbox']}
                label="Включить push-уведомления"
                value={isPushCheck}
                onValueChange={value => this.setState({ isPushCheck: value })}
              />
            )}
            {!this.props.isAuthenticated && (
              <div className={styles['license']}>
                <span>
                  {' '}
                  При подписке вы принимаете условия
                  <a className={styles['license-link']} target="_blank" href="//www.cian.ru/help/about/rules/">
                    {' '}
                    Пользовательского соглашения
                  </a>{' '}
                  и
                  <a className={styles['license-link']} target="_blank" href="//www.cian.ru/help/about/36526/">
                    {' '}
                    Политики конфиденциальности
                  </a>
                </span>
              </div>
            )}
          </form>
        )}
      </>
    );
  }
}
